import {
  useModal,
  Modal,
  Button,
  CloseSVG,
  DatePicker,
  useDatePickerHelpers,
} from "@newsela/angelou";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { isEqual } from "@s-libs/micro-dash";
import ReportFilterDropdown, {
  ReportFilterDropdownProps,
} from "@/reports/components/ReportFilterDropdown";
import useSearchParamsUpdate from "@/utils/useSearchParamsUpdate";
import useAdminReportingFiltersContext from "@/reports/admin/useAdminReportingFiltersContext/useAdminReportingFiltersContext";
import { getSelectedQueryValue } from "@/reports/utils/getSelectedQueryValue";
import {
  modalCss,
  modalWrapperCss,
  modalHeaderCss,
  modalTitleCss,
  modalBodyCss,
  modalFooterCss,
  datePickerCss,
  datePickerContainerCss,
  datePickerMessageCss,
} from "./ReportFilterDateRangeDropdown.style";
import {
  DateValue,
  formatDateValueToStringWithoutTime,
} from "./ReportFilterDateRangeDropdown.utils";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface ReportFilterDateRangeDropdownProps {
  allowCustomDateRange?: boolean;
}

const ReportFilterDateRangeDropdown = (
  props: ReportFilterDropdownProps & ReportFilterDateRangeDropdownProps,
) => {
  /**
   *  Nightingale allows a min_date delta that is greater than two years, defined as 365 * 2, then we must subtract 1 since it's greater than.
   *  https://github.com/newsela/nightingale/blob/60ce829ac182c8ad7125c3422456cc6ee54e29d0/nightingale/routes/v1/reading_skills/base_reading_skills_view.py#L19
   */
  const MIN_ALLOWED_DAYS_DELTA = 365 * 2 - 1;

  const router = useRouter();
  const { query } = router;

  const handleSearchParamsUpdate = useSearchParamsUpdate();

  const [modalProps, setActive] = useModal(false);

  const { getLocalTimeZone, today, formatDateValue, parseDate } =
    useDatePickerHelpers();
  const timeZone = getLocalTimeZone();

  const todaysDate = today(timeZone);
  const formattedTodaysDate = formatDateValue(todaysDate);

  const minDateInUrl = getSelectedQueryValue(query["min_date"]);
  const maxDateInUrl = getSelectedQueryValue(query["max_date"]);
  const dateRangeParamInUrl = getSelectedQueryValue(query["date_range"]);

  const isCustomDateRangeInUrl = dateRangeParamInUrl === "custom";
  const minDateAllowed = todaysDate.subtract({
    days: MIN_ALLOWED_DAYS_DELTA,
  });
  const formattedMinDateAllowed = formatDateValue(minDateAllowed);
  const { selectedFilters } = useAdminReportingFiltersContext();

  const initialStartDateValue = selectedFilters.dateRange?.[0];
  const initialEndDateValue = selectedFilters.dateRange?.[1];

  const parsedInitialStartDateValue = initialStartDateValue
    ? parseDate(initialStartDateValue)
    : null;
  const parsedInitialEndDateValue = initialEndDateValue
    ? parseDate(initialEndDateValue)
    : null;

  const [startDatePickerValue, setStartDateValue] = useState<DateValue>(
    parsedInitialStartDateValue,
  );
  const [endDatePickerValue, setEndDateValue] = useState<DateValue>(
    parsedInitialEndDateValue,
  );

  const [customDateRange, setCustomDateRange] = useState<
    [string, string] | null
  >();

  const [startDateError, setStartDateError] = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [startDateTouched, setStartDateTouched] = useState(false);
  const [endDateTouched, setEndDateTouched] = useState(false);

  const buttonContents =
    isCustomDateRangeInUrl && minDateInUrl && maxDateInUrl
      ? `${formatDateValue(parsedInitialStartDateValue)} - ${formatDateValue(parsedInitialEndDateValue)}`
      : null;

  const {
    menuOptions,
    eventTrackingDescription,
    allowCustomDateRange = false,
  } = props;

  const customRangeMenuOption = {
    value: "custom",
    label: "Select a custom range",
  };

  const menuOptionsWithCustomRange = [...menuOptions, customRangeMenuOption];

  const dateRangeMenuOptions = allowCustomDateRange
    ? menuOptionsWithCustomRange
    : menuOptions;

  const onReportFilterDropdownChange = (selectedOption: {
    value: string;
    label: string;
  }) => {
    if (selectedOption.value === customRangeMenuOption.value) {
      setActive(true);
    } else {
      setCustomDateRange(null);
    }
  };

  const startDatePickerOnBlur = () => {
    setStartDateTouched(true);
  };

  const endDatePickerOnBlur = () => {
    if (startDateTouched) validateDates();
    setEndDateTouched(true);
  };

  const validateDates = () => {
    setStartDateError("");
    setEndDateError("");

    // Both dates are required
    if (!startDatePickerValue) {
      setStartDateError(`Start date is required.`);
      return false;
    }

    if (!endDatePickerValue) {
      setEndDateError(`End date is required.`);
      return false;
    }

    // Dates must be greater than ~2 years ago
    if (startDatePickerValue?.compare(minDateAllowed) < 0) {
      setStartDateError(`Date can't be before ${formattedMinDateAllowed}.`);
      return false;
    }

    if (endDatePickerValue?.compare(minDateAllowed) < 0) {
      setEndDateError(`Date can't be before ${formattedMinDateAllowed}.`);
      return false;
    }

    // Dates must be in the past
    if (startDatePickerValue?.compare(todaysDate) > 0) {
      setStartDateError(`Date can't be after ${formattedTodaysDate}.`);
      return false;
    }

    if (endDatePickerValue?.compare(todaysDate) > 0) {
      setEndDateError(`Date can't be after ${formattedTodaysDate}.`);
      return false;
    }

    // Start date must be before end date
    if (startDatePickerValue.compare(endDatePickerValue) > 0) {
      setStartDateError(`Start date must be before end date.`);
      return false;
    }

    return true;
  };

  const onApplyDatesButtonClick = () => {
    if (!validateDates() || !startDatePickerValue || !endDatePickerValue)
      return;

    const formattedStartDate =
      formatDateValueToStringWithoutTime(startDatePickerValue);
    const formattedEndDate =
      formatDateValueToStringWithoutTime(endDatePickerValue);
    setCustomDateRange([formattedStartDate, formattedEndDate]);

    handleCloseModal();
  };

  const dropdownButtonRef = useRef<HTMLButtonElement>(null);

  const handleCloseModal = () => {
    setStartDateValue(parsedInitialStartDateValue);
    setEndDateValue(parsedInitialEndDateValue);
    setActive(false);

    // Focus back to the dropdown button after closing the modal
    setTimeout(() => {
      if (dropdownButtonRef?.current) {
        dropdownButtonRef.current.focus({
          preventScroll: true,
        });
      }
    }, 0);
  };

  useEffect(() => {
    if (
      customDateRange &&
      !isEqual(customDateRange, selectedFilters.dateRange)
    ) {
      handleSearchParamsUpdate({
        setParams: [
          { name: "date_range", value: customRangeMenuOption.value },
          { name: "min_date", value: customDateRange[0] },
          { name: "max_date", value: customDateRange[1] },
        ],
      });
    }
  }, [customDateRange]);

  useEffect(() => {
    if (!isCustomDateRangeInUrl && (minDateInUrl || maxDateInUrl)) {
      handleSearchParamsUpdate({
        deleteParams: [{ name: "min_date" }, { name: "max_date" }],
      });
    } else if (isCustomDateRangeInUrl && (!minDateInUrl || !maxDateInUrl)) {
      setActive(true);
    }
  }, [isCustomDateRangeInUrl]);

  return (
    <>
      <ReportFilterDropdown
        {...props}
        menuOptions={dateRangeMenuOptions}
        onChange={onReportFilterDropdownChange}
        buttonContents={buttonContents}
        valuesThatShouldNotTriggerUrlUpdate={[customRangeMenuOption.value]}
        dropdownButtonRef={dropdownButtonRef}
      />
      <Modal
        {...modalProps}
        onBackgroundClick={handleCloseModal}
        labelId="custom_date_range_modal"
        __cssFor={modalCss}
        onClose={handleCloseModal}
      >
        <div css={modalWrapperCss}>
          <div css={modalHeaderCss}>
            <h2 css={modalTitleCss}>Select custom date range</h2>
            <Button
              flavor={Button.flavors.incognito}
              size={Button.sizes.md}
              icon={{ SvgComponent: CloseSVG }}
              onClick={handleCloseModal}
              ariaProps={{
                "aria-label": "Close custom date range modal",
              }}
            />
          </div>

          <div css={modalBodyCss}>
            <div css={datePickerContainerCss}>
              <DatePicker
                __cssFor={datePickerCss}
                data-testid="ReportFilterDateRangeDropdown-StartDatePicker"
                error={startDateError}
                granularity="day"
                label="Start date"
                maxValue={todaysDate}
                minValue={minDateAllowed}
                onBlur={startDatePickerOnBlur}
                onChange={(value) => setStartDateValue(value)}
                shouldCloseOnSelect
                touched={startDateTouched}
                value={startDatePickerValue}
              />
              <DatePicker
                __cssFor={datePickerCss}
                data-testid="ReportFilterDateRangeDropdown-EndDatePicker"
                error={endDateError}
                granularity="day"
                label="End date"
                maxValue={todaysDate}
                minValue={startDatePickerValue}
                onBlur={endDatePickerOnBlur}
                onChange={(value) => setEndDateValue(value)}
                shouldCloseOnSelect
                touched={endDateTouched}
                value={endDatePickerValue}
              />
            </div>
            <div css={datePickerMessageCss}>
              Click on start and end dates fields above to set custom date range
              for your report.
            </div>
          </div>
          <div css={modalFooterCss}>
            <ButtonWithTrackEvent
              onClick={handleCloseModal}
              flavor={Button.flavors.secondary}
              actionName={actionNames.filter}
              objectType={objectTypes.report}
              actionPrefix={actionPrefixes.cancel}
              streamProperties={{
                description: eventTrackingDescription,
                report_filters: "cancel custom date range selection",
              }}
            >
              Cancel
            </ButtonWithTrackEvent>
            <ButtonWithTrackEvent
              onClick={onApplyDatesButtonClick}
              flavor={Button.flavors.primary}
              actionName={actionNames.filter}
              objectType={objectTypes.report}
              actionPrefix={actionPrefixes.initiate}
              streamProperties={{
                description: eventTrackingDescription,
                report_filters: "apply custom date range selection",
              }}
            >
              Apply dates
            </ButtonWithTrackEvent>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReportFilterDateRangeDropdown;
